import { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import useAppState from '../hooks/appStateHook';
import { AngularContext } from './AngularProvider';
import PropTypes from 'prop-types';

export const BroadcastContext = createContext({});

export const BroadcastProvider = ({ children }) => {
    const { token } = useAppState();
    const [listeners, setListeners] = useState({});
    const listenersRef = useRef({});
    const childWindows = useRef([]);
    
    const Angular = useContext(AngularContext);
    const $rootScope = Angular?.$rootScope;

    // Function to broadcast events to subscribed listeners
    const broadcast = useCallback((eventName, data, excludeWindowPropagation) => {
        if (listenersRef.current[eventName]) {
            listenersRef.current[eventName].forEach(listener => listener(data));
        }
        if ($rootScope) {
            //$rootScope.$broadcast(eventName, data);
        }
        if (!excludeWindowPropagation) {
            if (window.opener) {
                window.opener.postMessage({ type: eventName, params: data });
            }
            childWindows.current.forEach(child => {
                if (!child.closed) {
                    child.postMessage({ type: eventName, params: data })
                }
            });
            childWindows.current = childWindows.current.filter(child => !child.closed);
        }
    },[$rootScope,listenersRef]);

    useEffect(() => {
        const handler = event => {
            // pass true to not send the message back to the parent window
            if (event.data.type && event.data.params) {
                broadcast(event.data.type, event.data.params, true);
            }
        }
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [broadcast]);

    useEffect(() => {
        listenersRef.current = listeners;
    }, [listeners]);

    // Subscribe and unsubscribe to Angular events
    useEffect(() => {
        const unsubscribeHandlers = Object.keys(listeners).map(eventName => {
            const handler = (event, data) => {
                if (listenersRef.current[eventName]) {
                    listenersRef.current[eventName].forEach(listener => listener(data));
                }
            };

            // Subscribe to the Angular $rootScope event
            const cleanup = $rootScope ? $rootScope.$on(eventName, handler) : () => {};

            // Return the unsubscribe function for this handler
            return () => cleanup();
        });

        // Clean up all subscriptions when the component unmounts or listeners change
        return () => {
            unsubscribeHandlers.forEach(unsubscribe => unsubscribe());
        };
    }, [$rootScope, listeners]); // Re-subscribe when $rootScope or listeners change

    // Function to subscribe to an event
    const subscribe = useCallback((eventName, callback) => {
        setListeners(prevListeners => {
            const listenersForEvent = prevListeners[eventName] || [];
            if (listenersForEvent.every(c => c !== callback)) {
                return { ...prevListeners, [eventName]: [...listenersForEvent, callback] };
            } else {
                return prevListeners;
            }
        });
        if ($rootScope) {
            $rootScope.$on(eventName, (event, data) => {
                if (listenersRef.current[eventName]) {
                    listenersRef.current[eventName].forEach(listener => listener(data));
                }
            })
        }

        // Return an unsubscribe function
        return () => {
            setListeners(prevListeners => {
                const filteredListeners = prevListeners[eventName].filter(listener => listener !== callback);
                return { ...prevListeners, [eventName]: filteredListeners };
            });
        };
    }, [$rootScope,listenersRef]);

    const opener = useCallback((url, target, options) => {
        const child = window.open(url, target, options);
        if (childWindows.current.every(c => c !== child)) {
            childWindows.current.push(child);
            child.addEventListener("load", function () {
                // need a timeout to ensure that the child is listening before we fire the state synchornization
                setTimeout(() => {
                    broadcast('childOpened', { child }, true);
                }, 500);
            })
        }
        childWindows.current = childWindows.current.filter(child => !child.closed);
    }, [broadcast]);

    const popout = useCallback((sid, args) => {
        opener((process.env.NODE_ENV === 'production' ? process.env.PUBLIC_URL : '') + '/popout/' + sid + '?token=' + token + (args ? '&args=' + encodeURIComponent(JSON.stringify(args)) : ''), '_blank', 'popup=1')
    }, [opener, token])

    const value = useMemo(() => ({ broadcast, subscribe: subscribe, opener, popout }), [broadcast, subscribe, opener, popout]);

    return (
        <BroadcastContext.Provider value={value}>
            {children}
        </BroadcastContext.Provider>
    );
};

BroadcastProvider.propTypes = {
    children: PropTypes.node
}